// src/pages/Accounting/Reconcile/components/ResumeCard.tsx

import type React from "react";
import { PALETTE } from "../constants";
import type { Account, ReconciliationDraft } from "../types";
import { formatAccountLabel, formatCurrency, formatDateLong } from "../utils";

interface ResumeCardProps {
  draft: ReconciliationDraft;
  account: Account | undefined;
  onResume: () => void;
  onDiscard: () => void;
}

export function ResumeCard({ draft, account, onResume, onDiscard }: ResumeCardProps) {
  const currency = account?.currency ?? "CAD";
  const accountLabel = account ? formatAccountLabel(account) : "Unknown account";
  const savedDateLabel = formatSavedDate(draft.savedAt);
  const periodEndLabel = formatDateLong(draft.statementEndingDate);
  const endingBalanceLabel = formatCurrency(draft.statementEndingBalance, currency);

  return (
    <div style={cardStyle}>
      <div style={eyebrowStyle}>Picking up where you left off</div>
      <h2 style={titleStyle}>You have 1 reconciliation saved for later</h2>
      <div style={subtitleStyle}>
        Draft saved on <strong style={{ color: PALETTE.ink }}>{savedDateLabel}</strong>
      </div>

      <div style={fieldsWrapStyle}>
        <div style={accountFieldStyle}>
          <label style={labelStyle}>Account</label>
          <div style={lockedFieldStyle}>{accountLabel}</div>
        </div>

        <div style={metaRowStyle}>
          <div>
            <div style={metaLabelStyle}>Statement period</div>
            <div style={metaValueStyle}>Period Ending {periodEndLabel}</div>
          </div>
          <div>
            <div style={metaLabelStyle}>Ending balance</div>
            <div style={metaValueStyle}>
              {endingBalanceLabel} <span style={{ color: PALETTE.ink4, fontWeight: 500 }}>{currency}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={ctaRowStyle}>
        <button type="button" onClick={onResume} style={primaryButtonStyle}>
          Resume reconciling &rarr;
        </button>
        <button type="button" onClick={onDiscard} style={ghostButtonStyle}>
          Discard and start a new one
        </button>
      </div>
    </div>
  );
}

function formatSavedDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" });
}

const cardStyle: React.CSSProperties = {
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 12,
  background: "#fff",
  padding: "24px 28px",
  maxWidth: 820,
  marginBottom: 32,
};
const eyebrowStyle: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: PALETTE.tealDark,
  fontWeight: 700,
  marginBottom: 10,
};
const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: PALETTE.ink,
  margin: "0 0 6px",
  letterSpacing: "-0.01em",
};
const subtitleStyle: React.CSSProperties = {
  fontSize: 13.5,
  color: PALETTE.ink3,
  marginBottom: 20,
  fontWeight: 500,
};
const fieldsWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
const accountFieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink2,
  fontWeight: 600,
  marginBottom: 7,
};
const lockedFieldStyle: React.CSSProperties = {
  padding: "11px 14px",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 8,
  background: "#fff",
  fontSize: 14.5,
  color: PALETTE.ink,
  fontWeight: 500,
};
const metaRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 32,
  marginTop: 4,
};
const metaLabelStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: PALETTE.ink4,
  fontWeight: 600,
  marginBottom: 3,
};
const metaValueStyle: React.CSSProperties = {
  fontSize: 13.5,
  color: PALETTE.ink,
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
};
const ctaRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 14,
  alignItems: "center",
  marginTop: 24,
};
const primaryButtonStyle: React.CSSProperties = {
  background: "#0E1A1A",
  color: "#fff",
  border: 0,
  fontWeight: 600,
  fontSize: 13.5,
  padding: "11px 24px",
  borderRadius: 10,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
};
const ghostButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.ink3,
  fontWeight: 500,
  fontSize: 13.5,
  padding: "11px 8px",
  cursor: "pointer",
  fontFamily: "inherit",
};